import { chunk } from 'remeda'

import { assert } from '../assert/assert.js'
import { LazyPromise } from './types.js'

export interface PromiseAllOptions {
  concurrency: 'unbounded' | number | `batches-of-${number}`
}

export async function promiseAll<T extends unknown[]>(
  o: { [K in keyof T]: LazyPromise<T[K]> },
  options: PromiseAllOptions,
): Promise<{ [K in keyof T]: Awaited<T[K]> }>

export async function promiseAll<K extends PropertyKey, R extends Record<K, LazyPromise<unknown>>>(
  o: R,
  options: PromiseAllOptions,
): Promise<{ [P in keyof R]: Awaited<ReturnType<R[P]>> }>

export async function promiseAll(
  o: Record<PropertyKey, LazyPromise<unknown>> | LazyPromise<unknown>[],
  options: PromiseAllOptions,
): Promise<unknown> {
  if (Array.isArray(o)) {
    return promiseAllArray(o, options) as any
  }

  const entries = Object.entries(o)
  const lazyPromises = entries.map((e) => e[1])

  const values = await promiseAllArray(lazyPromises, options)

  const result = {} as any
  entries.forEach(([key], index) => {
    result[key] = values[index]
  })
  return result
}

async function promiseAllArray<T>(tasks: LazyPromise<T>[], options: PromiseAllOptions): Promise<Awaited<T>[]> {
  if (tasks.length === 0) {
    return []
  }

  if (options.concurrency === 'unbounded') {
    return Promise.all(tasks.map((t) => t()))
  }

  if (typeof options.concurrency === 'number') {
    return promiseAllArrayWithConcurrency(tasks, options.concurrency)
  }

  const prefix = 'batches-of-'
  assert(options.concurrency.startsWith(prefix))
  const batchSizes = Number.parseInt(options.concurrency.substring(prefix.length))
  assert(batchSizes >= 1, `Incorrect concurrency level: ${options.concurrency}`)

  const tasksBatches = chunk(tasks, batchSizes)
  let results: Awaited<T>[] = []
  for (const batch of tasksBatches) {
    results = results.concat(await promiseAllArray(batch, { concurrency: 'unbounded' }))
  }
  return results
}

function promiseAllArrayWithConcurrency<T>(tasks: LazyPromise<T>[], concurrency: number): Promise<Awaited<T>[]> {
  assert(concurrency >= 1, `Incorrect concurrency level: ${concurrency}`)

  const results = new Array<Awaited<T>>(tasks.length)
  let nextIndex = 0
  let running = 0
  let settled = false

  return new Promise<Awaited<T>[]>((resolve, reject) => {
    function maybeResolve(): void {
      if (!settled && nextIndex >= tasks.length && running === 0) {
        settled = true
        resolve(results)
      }
    }

    function failFast(err: unknown): void {
      if (!settled) {
        settled = true
        reject(err)
      }
    }

    function runNext(): void {
      if (settled) return

      const current = nextIndex++
      if (current >= tasks.length) {
        // No more to schedule; resolve when outstanding finish.
        maybeResolve()
        return
      }

      running++

      let p: Promise<T>
      try {
        p = tasks[current]!()
      } catch (err) {
        running--
        failFast(err)
        return
      }

      p.then((value) => {
        results[current] = value as Awaited<T>
        running--
        // Schedule another task if any remain.
        if (!settled) {
          runNext()
        }
        // If that was the last, resolve.
        maybeResolve()
      }).catch((err) => {
        running--
        failFast(err)
      })
    }

    // Prime the pool
    const workers = Math.min(concurrency as number, tasks.length)
    for (let i = 0; i < workers; i++) {
      runNext()
    }
  })
}
